import { useMemo } from "react";
import { z } from "zod";
import { observer } from "mobx-react-lite";
import { addStructureProperties } from "@/endpoints/dryp/addStructureProperties";
import type { StructurePropertyListItem } from "@/endpoints/dryp/structureProperty";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { validation } from "@/domain/validation";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { FormInput } from "@/components/forms/FormInput";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";

type PropertiesFormProps = {
	structureId: Dryp.StructureId;
	properties: StructurePropertyListItem[];
	onSaved: () => void;
};

type PropertiesFormValues = {
	values: Record<string, string>;
};

const isPropertyRequiredForIAndI = (property: StructurePropertyListItem) =>
	property.config.find(usage => usage.category === "IAndI")?.isRequired ?? true;

export const PropertiesForm = observer(({ structureId, properties, onSaved }: PropertiesFormProps) => {
	const saveApi = useAsyncStateStore(addStructureProperties);

	const schema = useMemo(
		() =>
			z.object({
				values: z.object(
					Object.fromEntries(
						properties.map(property => {
							const numberField = validation.numberField({
								min: property.minLimit,
								max: property.maxLimit,
							});
							return [
								String(property.typeId),
								isPropertyRequiredForIAndI(property) ? numberField : numberField.or(z.literal("")),
							];
						}),
					),
				),
			}) as unknown as z.ZodType<PropertiesFormValues>,
		[properties],
	);

	const defaultValues = useMemo<PropertiesFormValues>(
		() => ({ values: Object.fromEntries(properties.map(property => [String(property.typeId), ""])) }),
		[properties],
	);

	const { form, handleFormErrors } = useFormHandler<PropertiesFormValues>(schema, { defaultValues });

	const onSubmit = async (formValues: PropertiesFormValues) => {
		try {
			const filledProperties = properties
				.map(property => ({ typeId: property.typeId, rawValue: formValues.values[String(property.typeId)] }))
				.filter(entry => entry.rawValue != null && entry.rawValue !== "")
				.map(entry => ({ typeId: entry.typeId, value: Number(entry.rawValue) }));

			if (filledProperties.length > 0) await saveApi.fetch(structureId, filledProperties);
			onSaved();
		} catch (error) {
			handleFormErrors(error);
		}
	};

	return (
		<FormWrapper form={form} onSubmit={onSubmit} error={saveApi.error}>
			{properties.length === 0 ? (
				<p className="rounded-md bg-muted/40 px-3 py-6 text-center text-sm text-muted-foreground">
					No missing parameters for this structure.
				</p>
			) : (
				<div className="max-h-[24rem] space-y-4 overflow-y-auto pr-1">
					{properties.map(property => {
						const required = isPropertyRequiredForIAndI(property);
						return (
							<FormInput
								key={String(property.typeId)}
								name={`values.${property.typeId}`}
								type="number"
								label={`${property.typeName}${required ? "" : " (optional)"}${property.unit ? ` (${property.unit})` : ""}`}
								placeholder="Enter value"
								required={required}
							/>
						);
					})}
				</div>
			)}

			<FormSubmitButton isLoading={saveApi.loading} buttonText="Save & next" buttonLoadingText="Saving" />
		</FormWrapper>
	);
});
